import React, { useState, useEffect } from 'react'
import { api } from '@/utils/api'
import { getJalaliMonthName, formatHoursAndMinutes } from '@/utils/jalali'
import {
  ChartBarIcon,
  DocumentArrowDownIcon,
  ClockIcon,
  CheckCircleIcon,
  LockClosedIcon,
  SunIcon
} from '@heroicons/react/24/outline'
import { ConfirmModal, AlertModal } from '../components/ConfirmModal'

export const Reports: React.FC = () => {
  const [reports, setReports] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [exportLoading, setExportLoading] = useState<any>(null)

  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean
    title: string
    message: string
    confirmVariant?: 'danger' | 'primary' | 'warning'
    onConfirm: () => void
  }>({
    isOpen: false,
    title: '',
    message: '',
    confirmVariant: 'warning',
    onConfirm: () => {}
  })

  const [alertDialog, setAlertDialog] = useState<{
    isOpen: boolean
    title?: string
    message: string
    type?: 'success' | 'error' | 'info'
  }>({
    isOpen: false,
    title: '',
    message: '',
    type: 'info'
  })

  const fetchReports = async () => {
    try {
      const res = await api.get('/reports/monthly/list')
      setReports(res.data)
    } catch (error) {
      console.error('Failed to fetch reports:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchReports()
  }, [])

  const handleExport = async (report: any) => {
    setExportLoading(report.id)
    try {
      const res = await api.get(`/reports/monthly/${report.year}/${report.month}`)
      const d = res.data
      setAlertDialog({
        isOpen: true,
        type: 'info',
        title: `گزارش ${getJalaliMonthName(report.month)} ${report.year}`,
        message: `ساعت کارکرد کل: ${formatHoursAndMinutes(d.total_hours)}\nاضافه‌کاری: ${formatHoursAndMinutes(d.overtime_hours)}\nروزهای کاری: ${d.working_days} روز\nتعداد مرخصی‌ها: ${d.leaves?.length || 0} مورد`
      })
    } catch (error: any) {
      setAlertDialog({
        isOpen: true,
        type: 'error',
        title: 'خطا',
        message: error.response?.data?.detail || 'خطا در دریافت اطلاعات'
      })
    } finally {
      setExportLoading(null)
    }
  }

  const handleCloseMonth = (report: any) => {
    setConfirmDialog({
      isOpen: true,
      title: 'بستن دوره ماهانه',
      message: `آیا ماه ${getJalaliMonthName(report.month)} ${report.year} بسته شود؟ بعد از بسته شدن قابل ویرایش نخواهد بود.`,
      confirmVariant: 'warning',
      onConfirm: async () => {
        setConfirmDialog(prev => ({ ...prev, isOpen: false }))
        try {
          await api.post('/reports/monthly/close', null, {
            params: { year: report.year, month: report.month }
          })
          fetchReports()
          setAlertDialog({
            isOpen: true,
            type: 'success',
            title: 'موفقیت',
            message: 'ماه با موفقیت بسته شد.'
          })
        } catch (error: any) {
          setAlertDialog({
            isOpen: true,
            type: 'error',
            title: 'خطا',
            message: error.response?.data?.detail || 'خطا در بستن ماه'
          })
        }
      }
    })
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-600"></div>
      </div>
    )
  }

  const maxHours = Math.max(...reports.map(r => (r.total_hours || 0) + (r.overtime_hours || 0)), 160)

  return (
    <div className="space-y-6 max-w-xl mx-auto pb-20">
      {/* Header */}
      <div className="bg-white dark:bg-slate-800 rounded-3xl p-6 shadow-sm border border-slate-100 dark:border-slate-700/60 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 rounded-2xl">
            <ChartBarIcon className="w-6 h-6" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-800 dark:text-white">
              گزارش‌های کارکرد ماهانه
            </h1>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              مشاهده آمار کارکرد، اضافه‌کاری و مرخصی‌های هر ماه
            </p>
          </div>
        </div>
      </div>

      {/* Visual Statistics Chart */}
      {reports.length > 0 && (
        <div className="bg-white dark:bg-slate-800 rounded-3xl p-6 shadow-sm border border-slate-100 dark:border-slate-700/60 space-y-4">
          <div className="flex items-center justify-between pb-3 border-b border-slate-100 dark:border-slate-700">
            <h2 className="text-sm font-bold text-slate-800 dark:text-white flex items-center gap-2">
              <ChartBarIcon className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
              نمودار مقایسه‌ای ماه‌ها
            </h2>
            <span className="text-xs text-slate-400 font-semibold">ساعت کارکرد</span>
          </div>

          <div className="space-y-3 pt-1">
            {reports.slice(0, 6).map(r => {
              const workedPercent = Math.min(100, ((r.total_hours || 0) / maxHours) * 100)
              const overtimePercent = Math.min(100, ((r.overtime_hours || 0) / maxHours) * 100)
              const monthTitle = getJalaliMonthName(r.month) || `ماه ${r.month}`

              return (
                <div key={r.id} className="space-y-1.5">
                  <div className="flex justify-between text-xs font-semibold">
                    <span className="text-slate-700 dark:text-slate-200">{monthTitle} {r.year}</span>
                    <span className="text-slate-500 dark:text-slate-400">
                      {formatHoursAndMinutes(r.total_hours)} {r.overtime_hours > 0 ? `+ ${formatHoursAndMinutes(r.overtime_hours)} اضافه` : ''}
                    </span>
                  </div>
                  <div className="h-3 bg-slate-100 dark:bg-slate-900 rounded-full overflow-hidden flex">
                    <div
                      style={{ width: `${workedPercent}%` }}
                      className="bg-emerald-500 h-full rounded-r-full transition-all duration-500"
                    ></div>
                    {r.overtime_hours > 0 && (
                      <div
                        style={{ width: `${overtimePercent}%` }}
                        className="bg-amber-400 h-full rounded-l-full transition-all duration-500"
                      ></div>
                    )}
                  </div>
                </div>
              )
            })}
          </div>

          <div className="flex items-center justify-center gap-6 mt-4 pt-3 border-t border-slate-100 dark:border-slate-700 text-xs font-semibold text-slate-500 dark:text-slate-400">
            <div className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-emerald-500"></span>
              <span>ساعت موظفی</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-amber-400"></span>
              <span>اضافه‌کاری</span>
            </div>
          </div>
        </div>
      )}

      {/* Monthly Report List */}
      <div className="bg-white dark:bg-slate-800 rounded-3xl p-6 shadow-sm border border-slate-100 dark:border-slate-700/60 space-y-4">
        <h2 className="text-sm font-bold text-slate-800 dark:text-white pb-3 border-b border-slate-100 dark:border-slate-700">
          لیست دوره‌های ماهانه
        </h2>

        {reports.length === 0 ? (
          <div className="text-center py-8 text-slate-400 space-y-2">
            <ClockIcon className="w-10 h-10 mx-auto text-slate-300 dark:text-slate-600" />
            <p className="text-xs font-semibold">هنوز دوره‌ای برای نمایش ثبت نشده است.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {reports.map(report => {
              const isGenerating = exportLoading === report.id
              const monthName = getJalaliMonthName(report.month) || `ماه ${report.month}`
              const isClosed = report.status === 'closed'

              return (
                <div
                  key={report.id}
                  className="p-4 bg-slate-50 dark:bg-slate-900/40 rounded-2xl border border-slate-200/80 dark:border-slate-700/60 space-y-3"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-bold text-slate-800 dark:text-white text-sm">
                        {monthName} {report.year}
                      </h3>
                      <p className="text-[11px] text-slate-400 mt-0.5">
                        {formatHoursAndMinutes(report.total_hours)} کارکرد ماه
                      </p>
                    </div>
                    <span className={`px-2.5 py-1 text-[10px] font-bold rounded-full ${
                      isClosed 
                        ? 'bg-slate-200 text-slate-700 dark:bg-slate-800 dark:text-slate-300' 
                        : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300'
                    }`}>
                      {isClosed ? 'بسته شده' : 'جاری (باز)'}
                    </span>
                  </div>

                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div className="bg-white dark:bg-slate-800 rounded-xl p-2 border border-slate-100 dark:border-slate-700">
                      <p className="text-xs font-bold text-emerald-600 dark:text-emerald-400">{report.total_hours.toFixed(1)}h</p>
                      <p className="text-[10px] text-slate-400">کل ساعات</p>
                    </div>
                    <div className="bg-white dark:bg-slate-800 rounded-xl p-2 border border-slate-100 dark:border-slate-700">
                      <p className="text-xs font-bold text-amber-500">{report.overtime_hours.toFixed(1)}h</p>
                      <p className="text-[10px] text-slate-400">اضافه‌کاری</p>
                    </div>
                    <div className="bg-white dark:bg-slate-800 rounded-xl p-2 border border-slate-100 dark:border-slate-700">
                      <p className="text-xs font-bold text-blue-500">{report.leave_days}</p>
                      <p className="text-[10px] text-slate-400">مرخصی</p>
                    </div>
                  </div>

                  <div className="flex gap-2 pt-1">
                    <button
                      onClick={() => handleExport(report)}
                      disabled={isGenerating}
                      className="flex-1 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 rounded-xl text-xs font-bold hover:bg-slate-100 dark:hover:bg-slate-700 transition flex items-center justify-center gap-1.5 shadow-2xs"
                    >
                      <DocumentArrowDownIcon className="w-4 h-4" />
                      مشاهده جزئیات
                    </button>
                    {!isClosed && (
                      <button
                        onClick={() => handleCloseMonth(report)}
                        className="py-2 px-3 bg-rose-50 hover:bg-rose-100 text-rose-600 dark:bg-rose-950/40 dark:text-rose-300 rounded-xl text-xs font-bold transition flex items-center gap-1"
                      >
                        <LockClosedIcon className="w-3.5 h-3.5" />
                        بستن دوره
                      </button>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      <ConfirmModal
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmVariant={confirmDialog.confirmVariant}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog(prev => ({ ...prev, isOpen: false }))}
      />

      <AlertModal
        isOpen={alertDialog.isOpen}
        title={alertDialog.title}
        message={alertDialog.message}
        type={alertDialog.type}
        onClose={() => setAlertDialog(prev => ({ ...prev, isOpen: false }))}
      />
    </div>
  )
}

export default Reports
